import './quotes-entity-grid.scss';

import { Box, Link } from '@mui/material';
import {
  CircularLoader,
  ConvergeTooltip,
  DashboardHeader,
  FilterType,
  GridFilter,
  ISortProps,
  ITableColumn,
  PaginatedTable,
  PaperLayout,
} from '../../../presentation';
import {
  FilterIconType,
  QUOTES_FILTER_DEFAULTS,
  QUOTES_FILTER_OPTIONS,
  QUOTES_SORT_DEFAULTS,
  QuoteEntityFieldIdType,
  QuoteEntityFieldLabelType,
} from '@converge/data';
import { QuotesPageQuote, useGetQuotesPageQuery } from '@converge/graphql';

import { ConvergeNavigationUrls } from '../../../contexts';
import { formatDollarAmount } from '@converge/utils';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export const QuotesEntityGrid = () => {
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [quoteFilter, setQuoteFilter] = useState<FilterType>(
    QUOTES_FILTER_DEFAULTS
  );
  const [quoteSort, setQuoteSort] = useState<ISortProps>(QUOTES_SORT_DEFAULTS);

  const { loading, quotes, totalQuotes } = useGetQuotesPageQuery({
    pageNumber: currentPage,
    pageSize: rowsPerPage,
    sort: {
      key: quoteSort.id,
      order: quoteSort.order,
    },
    filters: {
      ...quoteFilter,
    },
  });
  const { t } = useTranslation();

  if (loading) {
    return <CircularLoader className="circular-loader-style" />;
  }

  const quoteColumns: ITableColumn<QuotesPageQuote, void>[] = [
    {
      id: QuoteEntityFieldIdType.ReferenceId,
      label: QuoteEntityFieldLabelType.ReferenceId,
      render: (row) => (
        <Box className="quoteColumns-reference-id">
          <ConvergeTooltip title={row.referenceId}>
            <Link
              className="reference-id"
              href={ConvergeNavigationUrls.quoteEntityDetail({
                quoteUuid: row.uuid,
              })}
            >
              {row.referenceId}
            </Link>
          </ConvergeTooltip>
        </Box>
      ),
      isSortable: true,
    },
    {
      id: QuoteEntityFieldIdType.SubmissionRefID,
      label: QuoteEntityFieldLabelType.SubmissionRefID,
      render: () => 'To be implemented',
      isSortable: true,
    },
    {
      id: QuoteEntityFieldIdType.InsuredName,
      label: QuoteEntityFieldLabelType.InsuredName,
      render: () => 'To be implemented',
      isSortable: true,
    },
    {
      id: QuoteEntityFieldIdType.Premium,
      label: QuoteEntityFieldLabelType.Premium,
      render: (row) => formatDollarAmount(row.quotedPremium) ?? 'N/A',
      isSortable: true,
    },
    {
      id: QuoteEntityFieldIdType.Limit,
      label: QuoteEntityFieldLabelType.Limit,
      render: (row) => formatDollarAmount(row.limit) ?? 'N/A',
      isSortable: true,
    },
    {
      id: QuoteEntityFieldIdType.Retention,
      label: QuoteEntityFieldLabelType.Retention,
      render: (row) => formatDollarAmount(row.retention) ?? 'N/A',
      isSortable: true,
    },
    {
      id: QuoteEntityFieldIdType.SIR,
      label: QuoteEntityFieldLabelType.SIR,
      render: (row) => {
        if (row.quotedPremium && row.ratedPremium) {
          return `${((row.quotedPremium / row.ratedPremium) * 100).toFixed(
            0
          )}%`;
        }
        return 'N/A';
      },
      isSortable: true,
    },
    {
      id: QuoteEntityFieldIdType.Underwriter,
      label: QuoteEntityFieldLabelType.Underwriter,
      render: () => 'To be implemented',
      isSortable: true,
    },
    {
      id: QuoteEntityFieldIdType.DocumentsLink,
      label: QuoteEntityFieldLabelType.DocumentsLink,
      render: () => 'To be implemented',
      isSortable: true,
    },
  ];
  return (
    <>
      <Box className="quotes-grid-header-title">
        <DashboardHeader
          title={t('entities.quotes.title', 'Quotes')}
          children={
            <div className="right-section">
              <GridFilter
                fields={QUOTES_FILTER_OPTIONS}
                values={quoteFilter}
                onFilterSubmit={(values) => {
                  setCurrentPage(0);
                  setQuoteFilter(values);
                }}
                icon={FilterIconType.FilledFilterIcon}
              />
            </div>
          }
        />
      </Box>
      <PaperLayout className="quotes-grid-layout">
        <PaginatedTable
          columns={quoteColumns}
          rows={quotes}
          enablePagination
          rowsPerPage={rowsPerPage}
          currentPage={currentPage}
          totalRows={totalQuotes ?? 0}
          handleChangePage={setCurrentPage}
          currentSort={quoteSort}
          handleSort={(sort) => {
            setQuoteSort(sort);
            setCurrentPage(0);
          }}
          handleChangeRowsPerPage={(newRowsPerPage) => {
            setRowsPerPage(newRowsPerPage);
            setCurrentPage(0);
          }}
          emptyRowMessage={t(
            'entities.quotes.grid.emptyMessage',
            'No quotes found'
          )}
        />
      </PaperLayout>
    </>
  );
};
